/**
 * Phase K.4 (2026-08-07) — Admin refund modal.
 *
 * Works for both statements and invoices. Callers pass:
 *   - ``kind``: "statement" | "invoice"
 *   - ``resource``: the loaded doc (needs id + total_cents + refunded_amount_cents)
 *   - ``open`` / ``onOpenChange``
 *   - ``onRefunded(result)``: fires after 202 with the API response
 *
 * The modal defaults amount to the full remaining refundable balance
 * (total - already-refunded) and disallows exceeding it (server
 * re-validates too — this is UX polish).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue,
    SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Undo2 } from "lucide-react";

const REASONS = [
    { value: "requested_by_customer", label: "Customer request" },
    { value: "duplicate", label: "Duplicate charge" },
    { value: "fraudulent", label: "Fraud" },
    { value: "other", label: "Other" },
];

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}

function parseCentsFromDollars(s) {
    // Accept "1,234.56" or "1234.5" → 123450 cents.
    const cleaned = String(s || "").replace(/[^0-9.]/g, "");
    if (!cleaned) return 0;
    const parts = cleaned.split(".");
    const dollars = parseInt(parts[0] || "0", 10);
    const centStr = (parts[1] || "").padEnd(2, "0").slice(0, 2);
    const cents = parseInt(centStr || "0", 10);
    return dollars * 100 + cents;
}
function centsToInputString(cents) {
    if (!cents) return "";
    const v = cents / 100;
    return v.toFixed(2);
}

export default function AdminRefundModal({
    kind, resource, open, onOpenChange, onRefunded,
}) {
    const totalPaid = resource?.total_cents ?? 0;
    const alreadyRefunded = resource?.refunded_amount_cents ?? 0;
    const maxRefundable = Math.max(0, totalPaid - alreadyRefunded);

    const [amount, setAmount] = useState(centsToInputString(maxRefundable));
    const [reason, setReason] = useState("requested_by_customer");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setAmount(centsToInputString(maxRefundable));
            setReason("requested_by_customer");
            setNotes("");
            setError(null);
            setSubmitting(false);
        }
    }, [open, maxRefundable]);

    const amountCents = useMemo(() => parseCentsFromDollars(amount), [amount]);
    const amountExceeds = amountCents > maxRefundable;
    const amountInvalid = amountCents <= 0;

    const handleSubmit = async () => {
        if (submitting) return;
        if (amountInvalid) {
            setError("Amount must be greater than $0.00.");
            return;
        }
        if (amountExceeds) {
            setError(`Amount exceeds refundable balance of ${fmtMoney(maxRefundable)}.`);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const path = kind === "invoice" ? "invoices" : "statements";
            const { data } = await api.post(
                `/admin/${path}/${resource.id}/refund`,
                { amount_cents: amountCents, reason, notes: notes || null },
            );
            toast.success(
                `Refund of ${fmtMoney(amountCents)} initiated — ${data.stripe_refund_id?.slice(0, 20)}…`,
            );
            onRefunded && onRefunded(data);
            onOpenChange && onOpenChange(false);
        } catch (err) {
            // Phase K.4.3 — surface a distinct message when Stripe's
            // API key is misconfigured. Every other structured Stripe
            // error falls through to formatApiError which pulls
            // ``detail.message``.
            const code = err?.response?.data?.detail?.code;
            if (code === "stripe_auth_error") {
                setError("Payment system misconfigured. Contact ops.");
            } else {
                setError(formatApiError(err));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[500px]"
                data-testid="admin-refund-modal"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Undo2 size={18} className="text-blue-900" />
                        Refund {kind === "invoice" ? "invoice" : "statement"}
                    </DialogTitle>
                    <DialogDescription>
                        Refunds the underlying Stripe PaymentIntent and
                        proportionally allocates a credit memo per
                        {kind === "statement" ? " constituent invoice." : " invoice."}
                        {" "}ACH refunds settle in 5-10 business days.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 text-sm">
                        <div className="flex items-center justify-between text-slate-600">
                            <span>Original paid</span>
                            <span className="tabular-nums font-medium text-slate-900">
                                {fmtMoney(totalPaid)}
                            </span>
                        </div>
                        {alreadyRefunded > 0 && (
                            <div className="flex items-center justify-between text-slate-600">
                                <span>Already refunded</span>
                                <span className="tabular-nums font-medium text-emerald-700">
                                    -{fmtMoney(alreadyRefunded)}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center justify-between pt-1 mt-1 border-t border-slate-200">
                            <span className="font-medium">Refundable</span>
                            <span className="tabular-nums font-semibold text-slate-900">
                                {fmtMoney(maxRefundable)}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="refund-amount" className="text-sm">
                            Amount (USD)
                        </Label>
                        <Input
                            id="refund-amount"
                            type="text" inputMode="decimal"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="0.00"
                            data-testid="admin-refund-amount"
                        />
                        {amountExceeds && (
                            <p className="text-xs text-red-700">
                                Exceeds refundable balance.
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-sm">Reason</Label>
                        <Select value={reason} onValueChange={setReason}>
                            <SelectTrigger data-testid="admin-refund-reason">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {REASONS.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>
                                        {r.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="refund-notes" className="text-sm">
                            Internal notes (optional)
                        </Label>
                        <Textarea
                            id="refund-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Case #4587 — refund per support call"
                            rows={2}
                            data-testid="admin-refund-notes"
                        />
                    </div>

                    {error && (
                        <div className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                             data-testid="admin-refund-error">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        type="button" variant="ghost"
                        onClick={() => onOpenChange && onOpenChange(false)}
                        disabled={submitting}
                        data-testid="admin-refund-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || amountInvalid || amountExceeds}
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="admin-refund-submit"
                    >
                        {submitting ? (
                            <><Loader2 size={14} className="animate-spin mr-2" /> Refunding…</>
                        ) : (
                            <>Refund {fmtMoney(amountCents)}</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
